/**
 * Icon Utilities for Map Layers
 *
 * Converts FontAwesome icons to images that can be used in Mapbox symbol layers.
 */

import { library, icon as faIcon, IconName, IconPrefix, findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';

// Add all solid icons to the library
library.add(fas);

/**
 * Icon style options for halo/outline effects
 */
export interface IconStyleOptions {
  /** Icon fill color */
  color?: string;
  /** Size multiplier (1 = 32px base) */
  size?: number;
  /** Halo/outline color (for visibility on varied backgrounds) */
  haloColor?: string;
  /** Halo/outline width in pixels (0 = no halo) */
  haloWidth?: number;
  /** Drop shadow blur radius (0 = no shadow) */
  shadowBlur?: number;
  /** Drop shadow color */
  shadowColor?: string;
}

/**
 * Cache for generated icon images to avoid regenerating
 */
const iconCache: Map<string, HTMLImageElement> = new Map();

/**
 * Look up a previously-rasterised icon image by its iconId. Used by layers
 * that need to re-`addImage()` synchronously on `style.load` (Mapbox's
 * per-style image cache is wiped on every setStyle).
 */
export function getCachedIconImage(iconId: string): HTMLImageElement | undefined {
  return iconCache.get(iconId);
}

/**
 * Generate a unique icon ID for Mapbox
 *
 * @param iconName FontAwesome icon name
 * @param options Icon style options
 * @returns Unique icon ID
 */
export function getIconId(iconName: string, options: IconStyleOptions = {}): string {
  const {
    color = '#3b82f6',
    size = 1,
    haloColor,
    haloWidth = 0,
    shadowBlur = 0
  } = options;

  // Normalize colors to hex without #
  const normalizedColor = color.replace('#', '');
  const normalizedHalo = haloColor?.replace('#', '') || 'none';

  return `fa-icon-${iconName}-${normalizedColor}-${size}-h${normalizedHalo}-${haloWidth}-s${shadowBlur}`;
}

/**
 * Legacy getIconId for backwards compatibility
 */
export function getIconIdLegacy(iconName: string, color: string, size: number = 1): string {
  return getIconId(iconName, { color, size });
}

/**
 * Convert a FontAwesome icon to an SVG string with optional halo/shadow effects
 *
 * @param iconName FontAwesome icon name (e.g., 'fire', 'map-marker-alt')
 * @param options Icon style options including color, size, halo, and shadow
 * @returns SVG string or null if icon not found
 */
export function faIconToSvg(iconName: string, options: IconStyleOptions = {}): string | null {
  const {
    color = '#3b82f6',
    size = 1,
    haloColor = '#ffffff',
    haloWidth = 0,
    shadowBlur = 0,
    shadowColor = 'rgba(0,0,0,0.5)'
  } = options;

  try {
    // Try to find the icon definition
    const iconDef = findIconDefinition({ prefix: 'fas' as IconPrefix, iconName: iconName as IconName });

    if (!iconDef) {
      console.warn(`[iconUtils] Icon not found: ${iconName}`);
      return null;
    }

    // Generate the icon
    const iconResult = faIcon(iconDef);

    if (!iconResult) {
      console.warn(`[iconUtils] Failed to generate icon: ${iconName}`);
      return null;
    }

    // Set dimensions based on size
    // Add padding for halo/shadow effects
    const baseSize = 32;
    const padding = Math.max(haloWidth * 2, shadowBlur) * size;
    const finalSize = Math.round(baseSize * size + padding * 2);

    // Calculate viewBox with padding
    const viewBoxWidth = iconDef.icon[0];
    const viewBoxHeight = iconDef.icon[1];
    const viewBoxPadding = (padding / size) * (viewBoxWidth / baseSize);
    const paddedViewBox = `-${viewBoxPadding} -${viewBoxPadding} ${viewBoxWidth + viewBoxPadding * 2} ${viewBoxHeight + viewBoxPadding * 2}`;

    // Build SVG with optional effects
    let defs = '';
    let shadowPath = '';
    let haloPath = '';

    // Add drop shadow filter if enabled
    // Using feGaussianBlur + feOffset for better compatibility when converting to image
    if (shadowBlur > 0) {
      // Scale shadow blur based on viewBox
      const scaledBlur = shadowBlur * (viewBoxWidth / baseSize);
      const scaledOffset = 2 * (viewBoxWidth / baseSize);
      defs += `
        <filter id="shadow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="${scaledBlur}" result="blur"/>
          <feOffset in="blur" dx="${scaledOffset}" dy="${scaledOffset}" result="offsetBlur"/>
          <feFlood flood-color="${shadowColor}" flood-opacity="0.5" result="color"/>
          <feComposite in="color" in2="offsetBlur" operator="in" result="shadow"/>
          <feMerge>
            <feMergeNode in="shadow"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>`;
    }

    // Add halo (stroke) if enabled - render behind the main icon
    if (haloWidth > 0) {
      // Scale stroke width based on viewBox size
      const strokeWidth = haloWidth * (viewBoxWidth / baseSize) * 2;
      haloPath = `<path fill="${haloColor}" stroke="${haloColor}" stroke-width="${strokeWidth}" stroke-linejoin="round" d="${iconDef.icon[4]}"/>`;
    }

    // Build the final SVG
    const filterAttr = shadowBlur > 0 ? ' filter="url(#shadow)"' : '';

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${finalSize}" height="${finalSize}" viewBox="${paddedViewBox}">
      ${defs ? `<defs>${defs}</defs>` : ''}
      <g${filterAttr}>
        ${haloPath}
        <path fill="${color}" d="${iconDef.icon[4]}"/>
      </g>
    </svg>`;

    return svg;
  } catch (error) {
    console.error(`[iconUtils] Error generating SVG for icon ${iconName}:`, error);
    return null;
  }
}

/**
 * Legacy faIconToSvg for backwards compatibility
 */
export function faIconToSvgLegacy(iconName: string, color: string = '#3b82f6', size: number = 1): string | null {
  return faIconToSvg(iconName, { color, size });
}

/**
 * Convert an SVG string to an HTMLImageElement
 *
 * @param svg SVG string
 * @returns Promise resolving to HTMLImageElement
 */
export function svgToImage(svg: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error(`Failed to load SVG image: ${e}`));

    // Convert SVG to data URL
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    img.src = dataUrl;
  });
}

/**
 * Load a FontAwesome icon into a Mapbox map
 *
 * @param map Mapbox map instance
 * @param iconName FontAwesome icon name
 * @param options Icon style options (color, size, halo, shadow)
 * @returns Promise resolving to the icon ID used in Mapbox
 */
export async function loadFaIconToMap(
  map: mapboxgl.Map,
  iconName: string,
  options: IconStyleOptions = {}
): Promise<string> {
  // Apply defaults
  const opts: IconStyleOptions = {
    color: '#3b82f6',
    size: 1.5,
    ...options
  };

  const iconId = getIconId(iconName, opts);

  // Check if icon is already loaded in map
  if (map.hasImage(iconId)) {
    return iconId;
  }

  // Check cache
  let img = iconCache.get(iconId);

  if (!img) {
    // Generate SVG with all style options
    const svg = faIconToSvg(iconName, opts);

    if (!svg) {
      throw new Error(`Failed to generate icon: ${iconName}`);
    }

    // Convert to image
    img = await svgToImage(svg);
    iconCache.set(iconId, img);
  }

  // Add to map
  map.addImage(iconId, img, { sdf: false });

  return iconId;
}

/**
 * Legacy loadFaIconToMap for backwards compatibility
 */
export async function loadFaIconToMapLegacy(
  map: mapboxgl.Map,
  iconName: string,
  color: string = '#3b82f6',
  size: number = 1.5
): Promise<string> {
  return loadFaIconToMap(map, iconName, { color, size });
}

/**
 * Remove a FontAwesome icon from a Mapbox map
 *
 * @param map Mapbox map instance
 * @param iconName FontAwesome icon name
 * @param options Icon style options
 */
export function removeFaIconFromMap(
  map: mapboxgl.Map,
  iconName: string,
  options: IconStyleOptions = {}
): void {
  const opts: IconStyleOptions = {
    color: '#3b82f6',
    size: 1.5,
    ...options
  };
  const iconId = getIconId(iconName, opts);

  if (map.hasImage(iconId)) {
    map.removeImage(iconId);
  }
}

/**
 * Get default icon for a given feature/layer type
 *
 * @param layerName Layer name for context
 * @returns Default icon name
 */
export function getDefaultIcon(layerName: string): string {
  const nameLower = layerName.toLowerCase();

  if (nameLower.includes('fire') || nameLower.includes('hotspot')) {
    return 'fire';
  }
  if (nameLower.includes('weather') || nameLower.includes('station')) {
    return 'cloud';
  }
  if (nameLower.includes('water') || nameLower.includes('hydrant')) {
    return 'droplet';
  }
  if (nameLower.includes('building') || nameLower.includes('structure')) {
    return 'building';
  }
  if (nameLower.includes('vehicle') || nameLower.includes('truck')) {
    return 'truck';
  }
  if (nameLower.includes('camp') || nameLower.includes('tent')) {
    return 'campground';
  }

  return 'map-marker-alt';
}
